/**
 * Pipeline screen — /app/pipeline, the manual outcome loop's home. A
 * button-only board (no drag-and-drop) over this tenant's `brand_claims`,
 * grouped by status: To contact / Contacted / Replied / Account opened,
 * with Dropped collapsed below as a muted, expandable list.
 *
 * Every transition is one explicit click through `useAdvanceClaim` (Task
 * 12) — the server enforces legality (routes/claims.ts's forward-only
 * transition table + race guard); a stale tab clicking an already-advanced
 * card surfaces the 400 ILLEGAL_TRANSITION as an inline card error rather
 * than failing silently.
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { BrandClaimStatus, ClaimResponse } from '@sourcito/schemas';
import { Badge, Button, Card } from '../../design';
import { daysIn } from '../../lib/bands';
import { useAdvanceClaim, useClaims } from '../../lib/feed';
import { AppShell } from '../../components/AppShell';
import { Skeleton } from '../../components/Skeleton';

type ActiveStatus = Exclude<BrandClaimStatus, 'dropped'>;

const COLUMNS: { status: ActiveStatus; label: string }[] = [
  { status: 'claimed', label: 'To contact' },
  { status: 'contacted', label: 'Contacted' },
  { status: 'replied', label: 'Replied' },
  { status: 'account_opened', label: 'Account opened' },
];

/** Forward-only, mirrors the server's transition table (routes/claims.ts). */
const NEXT_STATUS: Partial<Record<ActiveStatus, BrandClaimStatus>> = {
  claimed: 'contacted',
  contacted: 'replied',
  replied: 'account_opened',
};

const ADVANCE_LABEL: Partial<Record<ActiveStatus, string>> = {
  claimed: 'Mark contacted',
  contacted: 'Mark replied',
  replied: 'Account opened',
};

type AdvanceClaimMutation = ReturnType<typeof useAdvanceClaim>;

export function PipelinePage() {
  const { data, isLoading, isError, error } = useClaims();
  const advanceClaim = useAdvanceClaim();
  const [droppedOpen, setDroppedOpen] = useState(false);

  const claims = data?.claims ?? [];
  const active = claims.filter((c) => c.status !== 'dropped');
  const dropped = claims.filter((c) => c.status === 'dropped');

  return (
    <AppShell>
      <header className="border-b border-border-subtle">
        <div className="max-w-5xl mx-auto px-8 py-4">
          <h1 className="text-xl font-medium leading-tight">Pipeline</h1>
          <p className="text-xs text-fg-tertiary">
            {data ? `${active.length} active claim${active.length === 1 ? '' : 's'}` : ' '}
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-8 py-8 space-y-6">
        {isLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" aria-hidden="true">
            {[0, 1, 2, 3].map((i) => (
              <Card key={i}>
                <Skeleton className="h-3 w-1/2 rounded-xs" />
                <Skeleton className="h-4 w-3/4 rounded-xs mt-3" />
              </Card>
            ))}
          </div>
        )}

        {isError && (
          <Card className="border-negative/30 bg-negative/[0.06]">
            <p className="text-sm font-medium text-negative">Failed to load the pipeline</p>
            <p className="text-sm text-fg-secondary mt-1">
              {(error as Error)?.message ?? 'Unknown error'}
            </p>
          </Card>
        )}

        {data && claims.length === 0 && (
          <Card className="text-center py-14">
            <h2 className="text-base font-medium mb-2">Nothing claimed yet</h2>
            <p className="text-sm text-fg-secondary">
              Pick brands from{' '}
              <Link to="/app/brands" className="underline">
                the feed
              </Link>
              .
            </p>
          </Card>
        )}

        {data && claims.length > 0 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {COLUMNS.map((col) => (
                <PipelineColumn
                  key={col.status}
                  label={col.label}
                  claims={active.filter((c) => c.status === col.status)}
                  advanceClaim={advanceClaim}
                />
              ))}
            </div>

            {dropped.length > 0 && (
              <DroppedSection
                claims={dropped}
                open={droppedOpen}
                onToggle={() => setDroppedOpen((o) => !o)}
              />
            )}
          </>
        )}
      </main>
    </AppShell>
  );
}

function PipelineColumn({
  label,
  claims,
  advanceClaim,
}: {
  label: string;
  claims: ClaimResponse[];
  advanceClaim: AdvanceClaimMutation;
}) {
  return (
    <div>
      <h2 className="text-xs font-medium text-fg-tertiary uppercase tracking-wide mb-2">
        {label} <span className="tabular-nums">({claims.length})</span>
      </h2>
      <div className="space-y-3">
        {claims.length === 0 ? (
          <p className="text-xs text-fg-tertiary">—</p>
        ) : (
          claims.map((claim) => (
            <PipelineCard key={claim.id} claim={claim} advanceClaim={advanceClaim} />
          ))
        )}
      </div>
    </div>
  );
}

function PipelineCard({
  claim,
  advanceClaim,
}: {
  claim: ClaimResponse;
  advanceClaim: AdvanceClaimMutation;
}) {
  const nextStatus = NEXT_STATUS[claim.status as ActiveStatus];
  const advanceLabel = ADVANCE_LABEL[claim.status as ActiveStatus];

  // Both buttons on a card share the same mutation instance (house idiom —
  // see FeedPage's claimBrand/passBrand scoping), so pending/error state is
  // scoped by BOTH the claim id and the target status to tell "advancing"
  // apart from "dropping" on the same card.
  const advancing =
    advanceClaim.isPending &&
    advanceClaim.variables?.id === claim.id &&
    advanceClaim.variables?.status === nextStatus;
  const dropping =
    advanceClaim.isPending &&
    advanceClaim.variables?.id === claim.id &&
    advanceClaim.variables?.status === 'dropped';
  const errorMessage =
    advanceClaim.isError && advanceClaim.variables?.id === claim.id
      ? ((advanceClaim.error as Error)?.message ?? 'Update failed')
      : null;

  return (
    <Card>
      <Link
        to="/app/brands/$brandCacheId"
        params={{ brandCacheId: claim.brandCacheId }}
        className="font-medium hover:underline block truncate"
      >
        {claim.brandDisplayName}
      </Link>
      <p className="text-xs text-fg-tertiary mt-1">{daysIn(claim.updatedAt)}</p>

      {errorMessage && (
        <p role="alert" className="text-xs text-negative mt-2">
          {errorMessage}
        </p>
      )}

      <div className="flex items-center gap-2 mt-3">
        {nextStatus && advanceLabel && (
          <Button
            size="sm"
            loading={advancing}
            onClick={() => advanceClaim.mutate({ id: claim.id, status: nextStatus })}
          >
            {advanceLabel}
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          loading={dropping}
          onClick={() => advanceClaim.mutate({ id: claim.id, status: 'dropped' })}
        >
          Drop
        </Button>
      </div>
    </Card>
  );
}

function DroppedSection({
  claims,
  open,
  onToggle,
}: {
  claims: ClaimResponse[];
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border-t border-border-subtle pt-4">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="text-sm text-fg-tertiary hover:text-fg-secondary"
      >
        <span aria-hidden="true">{open ? '▾' : '▸'}</span> Dropped ({claims.length})
      </button>

      {open && (
        <ul className="mt-3 space-y-2">
          {claims.map((claim) => (
            <li key={claim.id} className="flex items-center justify-between gap-3 text-sm">
              <Link
                to="/app/brands/$brandCacheId"
                params={{ brandCacheId: claim.brandCacheId }}
                className="text-fg-tertiary hover:text-fg-secondary hover:underline truncate"
              >
                {claim.brandDisplayName}
              </Link>
              <span className="flex items-center gap-2 shrink-0">
                <Badge variant="neutral">Dropped</Badge>
                <span className="text-xs text-fg-tertiary">{daysIn(claim.updatedAt)}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
